import React, { useCallback } from 'react';
import { useAsyncValue, useRevalidator } from 'react-router-dom';
import { useRevalidateOnSync } from '../../../../offline/useRevalidateOnSync';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components
import { ValidationsTable } from '../../../../components/display/tables/ValidationsTable';
import { MainButton } from '../../../../components/inputs/buttons/MainButton';
// Styles
import { Container, ContainerButton } from '../../../main/workOrders/components/MainStyledComponents';

// Types
import {
  tableIds,
  takeBase,
} from '../../../../components/display/tables/types';
import { firstPageTake } from '../../../../utils/cursor-take.helper';
import { dataTypes } from '../../../../components/display/tables/ValidationsTable/types';

// Utils
import { modalIds } from '../../../../components/modals/SuperModal/types';
import { openModal } from '../../../../state/actions/ui/modal';

// Hooks
import { usePersistentSelection } from '../../../../hooks/usePersistentSelection';
import { usePollingRevalidation } from '../../../../hooks/usePollingRevalidation';

// GQL
import { client } from '../../../../gql/client';
import { FETCH_ACTIONS } from '../../../../gql/queries/actions/actions';
import { selectActionOrders } from '../../../../gql/selectors/actions';

// Redux
import { getCursor } from '../../../../state/selectors/ui/tables';
import { setCursor } from '../../../../state/actions/ui/cursorTables';
import { store } from '../../../../state/configureStore';

export default function HistoricalActionsReportPage(): JSX.Element {
  const { t } = useTranslation(['pages', 'tables']);
  const dispatch = useDispatch();
  const data = useAsyncValue() as dataTypes;
  const revalidator = useRevalidator();
  useRevalidateOnSync();

  // Persistent selection — all state managed by the hook
  const {
    rowSelection,
    onRowSelectionChange,
    selectedCount,
    selectAllMode,
    excludedIds,
    selectedRows,
    handleSelectAll,
    handleClearSelection,
  } = usePersistentSelection({
    dataAll: data.actions || [],
    totalCount: data.totalCount || 0,
  });

  const onReFetchData = useCallback(async () => {
    const currentCursor = getCursor(tableIds.REPORTING_ACTIONS)(
      store.getState()
    );
    // Tarea #11: respetar el take guardado en cursor (page-size selector).
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    const { data } = await client.query({
      query: FETCH_ACTIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor?.sortField,
          orderBy: currentCursor?.orderBy ?? 'desc',
          concessionId: currentCursor?.concessionId,
          toDate: currentCursor?.toDate,
          fromDate: currentCursor?.fromDate,
          indexType: currentCursor?.indexType,
          indexExpedition: currentCursor?.indexExpedition,
          classification: currentCursor?.classification,
          predefinedOrderId: currentCursor?.predefinedOrderId,
          capitolId: currentCursor?.capitolId,
          subCapitolId: currentCursor?.subCapitolId,
          states: currentCursor?.states,
          userIds: currentCursor?.userIds,
          operatorIds: currentCursor?.operatorIds,
          teamIds: currentCursor?.teamIds,
          externalCompanyId: currentCursor?.externalCompanyId,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
          track: currentCursor?.track,
          activityId: currentCursor?.activityId,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: currentCursor?.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });

    dispatch(
      setCursor(tableIds.REPORTING_ACTIONS, {
        take: effectiveTake,
        cursor: '',
        concessionId: currentCursor?.concessionId,
        toDate: currentCursor?.toDate,
        fromDate: currentCursor?.fromDate,
        indexType: currentCursor?.indexType,
        indexExpedition: currentCursor?.indexExpedition,
        classification: currentCursor?.classification,
        predefinedOrderId: currentCursor?.predefinedOrderId,
        capitolId: currentCursor?.capitolId,
        subCapitolId: currentCursor?.subCapitolId,
        states: currentCursor?.states,
        userIds: currentCursor?.userIds,
        operatorIds: currentCursor?.operatorIds,
        externalCompanyId: currentCursor?.externalCompanyId,
        pkInitKm: currentCursor?.pkInitKm,
        pkInitMeter: currentCursor?.pkInitMeter,
        pkEndKm: currentCursor?.pkEndKm,
        pkEndMeter: currentCursor?.pkEndMeter,
        track: currentCursor?.track,
        activityId: currentCursor?.activityId,
        onlyFlagged: currentCursor?.onlyFlagged,
      })
    );

    await selectActionOrders(data.actions.actions, true);
    revalidator.revalidate();
  }, []);

  const handleDownloadPDF = useCallback(() => {
    const currentCursor = getCursor(tableIds.REPORTING_ACTIONS)(store.getState());
    const { id: _id, take: _take, cursor: _cursor, ...filterFields } = currentCursor || {} as any;
    dispatch(
      openModal(modalIds.DOWNLOAD_HISTORIC, {
        type: 'Action',
        filterPositionColumn: 1,
        selectedRows: selectAllMode ? [] : selectedRows,
        showReport: true,
        selectAll: selectAllMode,
        excludedIds: selectAllMode ? [...excludedIds] : [],
        filters: filterFields,
        totalCount: data.totalCount || 0,
      })
    );
  }, [selectedRows, dispatch, selectAllMode, excludedIds, data.totalCount]);

  const onRefetchPagination = useCallback(async () => {
    revalidator.revalidate();
  }, [revalidator]);

  // Polling: refresca datos de pág 1 en cache (network-only) sin cambiar posición del usuario
  const onPollingRefresh = useCallback(async () => {
    const currentCursor = getCursor(tableIds.REPORTING_ACTIONS)(store.getState());
    const effectiveTake = firstPageTake(currentCursor.take, takeBase);
    await client.query({
      query: FETCH_ACTIONS,
      variables: {
        input: {
          take: effectiveTake,
          cursor: '',
          sortField: currentCursor?.sortField,
          orderBy: currentCursor?.orderBy ?? 'desc',
          concessionId: currentCursor?.concessionId,
          toDate: currentCursor?.toDate,
          fromDate: currentCursor?.fromDate,
          indexType: currentCursor?.indexType,
          indexExpedition: currentCursor?.indexExpedition,
          classification: currentCursor?.classification,
          predefinedOrderId: currentCursor?.predefinedOrderId,
          capitolId: currentCursor?.capitolId,
          subCapitolId: currentCursor?.subCapitolId,
          states: currentCursor?.states,
          userIds: currentCursor?.userIds,
          operatorIds: currentCursor?.operatorIds,
          teamIds: currentCursor?.teamIds,
          externalCompanyId: currentCursor?.externalCompanyId,
          pkInitKm: currentCursor?.pkInitKm,
          pkInitMeter: currentCursor?.pkInitMeter,
          pkEndKm: currentCursor?.pkEndKm,
          pkEndMeter: currentCursor?.pkEndMeter,
          track: currentCursor?.track,
          activityId: currentCursor?.activityId,
          // Sin esto la lista filtrada dejaría de refrescarse en silencio.
          onlyFlagged: currentCursor?.onlyFlagged,
        },
      },
      fetchPolicy: 'network-only',
    });
    // NO despachar setCursor — el usuario sigue donde está
    revalidator.revalidate();
  }, []);
  usePollingRevalidation(onPollingRefresh);

  return (
    <Container>
      <ValidationsTable
        id={'historical-Action'}
        titleText={t('pages:actions.filterBy')}
        data={data}
        onReFetchData={onReFetchData}
        onReFetchPagination={onRefetchPagination}
        tableId={tableIds.REPORTING_ACTIONS}
        selectionMode="checkbox"
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        selectAllMode={selectAllMode}
        excludedIds={excludedIds}
        onSelectAll={handleSelectAll}
        onClearSelection={handleClearSelection}
        selectedCount={selectedCount}
        // Banderitas (spec 2026-10-22 § 8): columna y filtro «Solo marcadas».
        showFlagColumn
        showOnlyFlaggedFilter
      />
      <ContainerButton>
        <MainButton
          text={t('pages:actions.downloadHistoric')}
          disabled={selectedCount === 0}
          onClick={handleDownloadPDF}
        />
      </ContainerButton>
    </Container>
  );
}
